import * as React from 'react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { API_BASE_URL } from '../config/config.js';

import {
  Box, Paper, Typography, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, CircularProgress, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  MenuItem, Select, FormControl, InputLabel, Chip,
  Alert, InputAdornment, Tabs, Tab, Checkbox, FormGroup,
  FormControlLabel, Divider, Card, CardContent
} from '@mui/material';
import {
  Delete, Refresh, Search, Edit, PersonAdd,
  AdminPanelSettings, Person, Visibility, Engineering,
  Security, RestartAlt
} from '@mui/icons-material';

const AxiosAPI = axios.create({ baseURL: API_BASE_URL });

const ROLES = [
  { value: 'admin', label: 'Admin', color: 'error', icon: <AdminPanelSettings fontSize="small" /> },
  { value: 'ops', label: 'Operations', color: 'warning', icon: <Engineering fontSize="small" /> },
  { value: 'ict', label: 'ICT', color: 'secondary', icon: <Security fontSize="small" /> },
  { value: 'user', label: 'User', color: 'primary', icon: <Person fontSize="small" /> },
  { value: 'viewer', label: 'Viewer', color: 'default', icon: <Visibility fontSize="small" /> },
];

const ALL_TABS = [
  { id: 'manage-ads', label: 'Manage Ads' },
  { id: 'special-announcements', label: 'Special Announcements' },
  { id: 'accessibility', label: 'Accessibility' },
  { id: 'airlines', label: 'Airlines' },
  { id: 'fleet', label: 'Fleet' },
  { id: 'observability', label: 'Observability' },
  { id: 'baggage-gate', label: 'Baggage & Gate' },
  { id: 'status-override', label: 'STATUS' },
  { id: 'incidents', label: 'INCIDENTS' },
  { id: 'user-management', label: 'User Management' },
];

const UserManagement = () => {
  const getAuthToken = () => {
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.token;
      }
    } catch (error) {
      console.error('Error reading auth token:', error);
    }
    return null;
  };

  const authToken = getAuthToken();

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Users state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });

  // Role permissions state
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Form state for editing user
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'user',
    isActive: true,
    isEmailVerified: false,
  });

  // Form state for creating user
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'user',
  });

  useEffect(() => {
    if (authToken) {
      AxiosAPI.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      loadUsers();
      loadRolePermissions();
    }
  }, []);

  // ============== USERS ==============

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await AxiosAPI.get('/api/admin/users', {
        params: { search: searchQuery }
      });

      if (response.data.success) {
        setUsers(response.data.data || []);
      } else {
        toast.error(response.data.message || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email,
      role: user.role,
      isActive: user.isActive,
      isEmailVerified: user.isEmailVerified,
    });
    setEditDialog({ open: true, user });
  };

  const handleSaveEdit = async () => {
    try {
      const response = await AxiosAPI.put(`/api/admin/users/${editDialog.user._id}`, editForm);

      if (response.data.success) {
        toast.success('User updated successfully');
        setEditDialog({ open: false, user: null });
        loadUsers();
      } else {
        toast.error(response.data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password) {
      toast.error('Email and password are required');
      return;
    }

    try {
      const response = await AxiosAPI.post('/api/admin/users', createForm);

      if (response.data.success) {
        toast.success('User created successfully');
        setCreateDialog(false);
        setCreateForm({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          role: 'user',
        });
        loadUsers();
      } else {
        toast.error(response.data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      const errData = error.response?.data;
      if (errData?.errors?.length) {
        toast.error(errData.errors.join(' • '));
      } else {
        toast.error(errData?.message || 'Failed to create user');
      }
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await AxiosAPI.delete(`/api/admin/users/${deleteDialog.user._id}`);

      if (response.data.success) {
        toast.success('User deleted successfully');
        setDeleteDialog({ open: false, user: null });
        loadUsers();
      } else {
        toast.error(response.data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  // ============== ROLE PERMISSIONS ==============

  const loadRolePermissions = async () => {
    try {
      setLoadingPermissions(true);
      const response = await AxiosAPI.get('/api/admin/role-permissions');

      if (response.data.success) {
        setRolePermissions(response.data.data || []);
      } else {
        toast.error(response.data.message || 'Failed to load role permissions');
      }
    } catch (error) {
      console.error('Error loading role permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to load role permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleTabPermissionChange = (role, tabId, checked) => {
    setRolePermissions(prev => prev.map(perm => {
      if (perm.role !== role) return perm;

      let newTabs = [...perm.allowedTabs];
      if (checked) {
        if (!newTabs.includes(tabId)) {
          newTabs.push(tabId);
        }
      } else {
        newTabs = newTabs.filter(t => t !== tabId);
      }

      return { ...perm, allowedTabs: newTabs };
    }));
  };

  const handleSavePermissions = async (role) => {
    const perm = rolePermissions.find(p => p.role === role);
    if (!perm) return;

    try {
      setSavingPermissions(true);
      const response = await AxiosAPI.put(`/api/admin/role-permissions/${role}`, {
        allowedTabs: perm.allowedTabs
      });

      if (response.data.success) {
        toast.success(`Permissions saved for ${role} role`);
      } else {
        toast.error(response.data.message || 'Failed to save permissions');
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!window.confirm('Are you sure you want to reset all permissions to defaults?')) {
      return;
    }

    try {
      setSavingPermissions(true);
      const response = await AxiosAPI.post('/api/admin/role-permissions/reset');

      if (response.data.success) {
        toast.success('Permissions reset to defaults');
        setRolePermissions(response.data.data || []);
      } else {
        toast.error(response.data.message || 'Failed to reset permissions');
      }
    } catch (error) {
      console.error('Error resetting permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to reset permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  // ============== HELPERS ==============

  const getRoleChip = (role) => {
    const roleConfig = ROLES.find(r => r.value === role) || ROLES[2];
    return (
      <Chip
        icon={roleConfig.icon}
        label={roleConfig.label}
        color={roleConfig.color}
        size="small"
        sx={{ minWidth: 100 }}
      />
    );
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.firstName && user.firstName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (user.lastName && user.lastName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // ============== RENDER ==============

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        User Management
      </Typography>

      {/* Sub-tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
          <Tab icon={<Person />} label="Users" iconPosition="start" />
          <Tab icon={<Security />} label="Role Permissions" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Users Tab */}
      {currentTab === 0 && (
        <>
          {/* Toolbar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadUsers()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadUsers}
                disabled={loading}
              >
                Refresh
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => setCreateDialog(true)}
              >
                Add User
              </Button>
            </Box>
          </Paper>

          {/* Users Table */}
          <TableContainer component={Paper} sx={{ backgroundColor: '#000' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#2a2a2a' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Last Login</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, backgroundColor: '#1a1a1a' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, backgroundColor: '#1a1a1a', color: '#999' }}>
                      <Typography sx={{ color: '#999' }}>No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user, index) => (
                    <TableRow key={user._id} hover sx={{ backgroundColor: index % 2 === 0 ? '#1a1a1a' : '#000', '&:hover': { backgroundColor: '#2a2a2a' } }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{user.email}</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {user.firstName || user.lastName
                          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{getRoleChip(user.role)}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            color={user.isActive ? 'success' : 'default'}
                            size="small"
                            variant="outlined"
                          />
                          {user.isEmailVerified && (
                            <Chip
                              label="Verified"
                              color="info"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleString()
                          : 'Never'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit user">
                          <IconButton onClick={() => handleEditUser(user)} size="small" sx={{ color: '#fff' }}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete user">
                          <IconButton
                            onClick={() => setDeleteDialog({ open: true, user })}
                            size="small"
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {filteredUsers.length} users
            </Typography>
          </Box>
        </>
      )}

      {/* Role Permissions Tab */}
      {currentTab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestartAlt />}
              onClick={handleResetPermissions}
              disabled={savingPermissions}
            >
              Reset to Defaults
            </Button>
          </Box>

          {loadingPermissions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {ROLES.map((roleConfig) => {
                const perm = rolePermissions.find(p => p.role === roleConfig.value);
                const allowedTabs = perm?.allowedTabs || [];

                return (
                  <Card key={roleConfig.value} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleChip(roleConfig.value)}
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {perm?.description || ''}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSavePermissions(roleConfig.value)}
                          disabled={savingPermissions || roleConfig.value === 'admin'}
                        >
                          Save
                        </Button>
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Allowed Tabs:
                      </Typography>

                      <FormGroup row>
                        {ALL_TABS.map((tab) => {
                          const isChecked = allowedTabs.includes(tab.id);
                          const isDisabled = roleConfig.value === 'admin' ||
                            (tab.id === 'user-management' && roleConfig.value === 'admin');

                          return (
                            <FormControlLabel
                              key={tab.id}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => handleTabPermissionChange(
                                    roleConfig.value,
                                    tab.id,
                                    e.target.checked
                                  )}
                                  disabled={isDisabled}
                                />
                              }
                              label={tab.label}
                              sx={{ minWidth: 180 }}
                            />
                          );
                        })}
                      </FormGroup>

                      {roleConfig.value === 'admin' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Admin role always has access to all tabs and cannot be modified.
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editForm.role}
                label="Role"
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
              >
                {ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.icon}
                      {role.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.isActive}
                  label="Status"
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value })}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Email Verified</InputLabel>
                <Select
                  value={editForm.isEmailVerified}
                  label="Email Verified"
                  onChange={(e) => setEditForm({ ...editForm, isEmailVerified: e.target.value })}
                >
                  <MenuItem value={true}>Verified</MenuItem>
                  <MenuItem value={false}>Not Verified</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, user: null })}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              fullWidth
              required
              helperText="Min 8 chars, must include uppercase, lowercase, number, and special character (@$!%*?&)"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={createForm.firstName}
                onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={createForm.lastName}
                onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={createForm.role}
                label="Role"
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
              >
                {ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.icon}
                      {role.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete the user <strong>{deleteDialog.user?.email}</strong>?
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
